import styles from "./inventory-page.module.css";

import { useInventoryContext } from "@/contexts/InventoryContext";
import { useEffect, useMemo } from "react";
import { useUserContext } from "@/contexts/UserContext";
import Skeleton from "@/components/Skeleton/Skeleton";
import SellerAccountBanner from "@/components/SellerAccountBanner/SellerAccountBanner";
import InventoryTable from "../InventoryTable/InventoryTable";
import { formatValue } from "@/utils/formatUtils";
import useFeatureFlag from "@/hooks/useFeatureflag";
import IconPlus from "@/components/icons/IconPlus";
import Flex from "@/components/Flex/Flex";
import { useFbaListingContext } from "@/contexts/FbaListingContext";
import { useFbmListingContext } from "@/contexts/FbmListingContext";
import { useLocation } from "wouter";
import { formatISODate } from "@/utils/dateUtils";
import LastUpdatedTimeStamp from "./LastUpdatedTimeStamp/LastUpdatedTimeStamp";
import Button from "@/components/forms/Button";
import Tooltip from "@/components/Tooltip";
import { UserRole } from "@boxem/core-api-schema";

type Stat = {
  label: string;
  value: string;
};

type Analytic = {
  label?: string;
  value?: string;
  stats: Stat[];
  fba?: string;
  fbm?: string;
};

type InventoryPageProps = {};

const InventoryPage = ({}: InventoryPageProps) => {
  const { analytics, refetchAnalytics, isFetchingAnalytics, isFetchingInventory } = useInventoryContext();
  const { seller, user } = useUserContext();
  const isNewSideBar = useFeatureFlag("newSideBar");
  const [location, navigate] = useLocation();
  const { resetFbaListingFlow } = useFbaListingContext();
  const { resetFbmListingFlow } = useFbmListingContext();

  const handleListFba = () => {
    navigate("/fba/listings/create");
    resetFbaListingFlow(); //We will reset the flow if the user presses the "List FBA" option in the dropdown.
  };

  const handleListFbm = () => {
    navigate("/fbm/listings/create");
    resetFbmListingFlow(); //We will reset the flow if the user presses the "List FBA" option in the dropdown.
  };

  const getTotal = (key: string) => {
    if (!analytics || !key) {
      return 0;
    }

    const fbaValue = analytics.fba[key] ? analytics.fba[key] : 0;
    const fbmValue = analytics.fbm[key] ? analytics.fbm[key] : 0;

    return fbaValue + fbmValue;
  };

  const cardAnalytics = useMemo((): Analytic[] => [
    {
      label: "Total Amount of SKUs",
      value: formatValue((analytics?.totalSKUs ? analytics.totalSKUs : 0), "number"),
      stats: [
        {
          label: "In-Stock SKUs",
          value: formatValue(getTotal("totalSKUs"), "number"),
        },
        {
          label: "Out of Stock SKUs",
          value: formatValue((analytics?.totalSKUs ? analytics.totalSKUs - getTotal("totalSKUs") : 0), "number"),
        }
      ],
    },
    {
      label: "In-stock quantity",
      value: formatValue(getTotal("totalInStockQuantity"), "number"),
      stats: [
        {
          label: "FBA",
          value: formatValue((analytics?.fba?.totalInStockQuantity ? analytics.fba.totalInStockQuantity : 0), "number"),
        },
        {
          label: "FBM",
          value: formatValue((analytics?.fbm?.totalInStockQuantity ? analytics.fbm.totalInStockQuantity : 0), "number"),
        }
      ],
    },
    {
      label: "In-stock sales value",
      value: formatValue(getTotal("inStockSalesValue"), "currency"),
      stats: [
        {
          label: "FBA",
          value: formatValue((analytics?.fba?.inStockSalesValue ? analytics.fba.inStockSalesValue : 0.0), "currency"),
        },
        {
          label: "FBM",
          value: formatValue((analytics?.fbm?.inStockSalesValue ? analytics.fbm.inStockSalesValue : 0.0), "currency"),
        }
      ],
    },
    {
      label: "In-stock buy cost",
      value: formatValue(getTotal("inStockBuyCost"), "currency"),
      stats: [
        {
          label: "FBA",
          value: formatValue((analytics?.fba?.inStockBuyCost ? analytics.fba.inStockBuyCost : 0.0), "currency"),
        },
        {
          label: "FBM",
          value: formatValue((analytics?.fbm?.inStockBuyCost ? analytics.fbm.inStockBuyCost : 0.00), "currency"),
        }
      ],
    },
  ], [analytics]);

  useEffect(() => {
    if (seller && !isFetchingAnalytics) {
      refetchAnalytics();
    }
  }, [seller]);
  
  return (
    <div className={styles.pageContent}>
      {isNewSideBar ? 
      <div className={styles.ctaContainer}>
        <Flex direction="row" gap={4}>
          <Tooltip
            title={!user.sellerId && user.role !== UserRole.ASSISTANT ? "Connect your Amazon Seller Account first!" : ""}
          >
            <Button
              onClick={handleListFbm}
              disabled={!user.sellerId && user.role !== UserRole.ASSISTANT}
            >
              <IconPlus size={16} />
              Create FBM Listings
            </Button>
          </Tooltip>
          
          <Tooltip
            title={!user.sellerId && user.role !== UserRole.ASSISTANT ? "Connect your Amazon Seller Account first!" : ""}
          >
            <Button
              onClick={handleListFba}
              disabled={!user.sellerId && user.role !== UserRole.ASSISTANT}
            >
              <IconPlus size={16} />
              Create FBA Listings
            </Button>
          </Tooltip>
        </Flex>
      </div> : null}

      <SellerAccountBanner />
      <div className={styles.cardsContainer}>
        {cardAnalytics.map((analytic, index) => {
          return (
            <div key={index} className={styles.card}>
              <div className={styles.cardLabel}>{analytic.label}</div>
              <div className={styles.cardValue}>
                {isFetchingAnalytics || isFetchingInventory ? <Skeleton type='line-medium' /> : analytic.value}
              </div>
              <div className={styles.dividerContainer}>
                <div className={styles.divider} />
              </div>
              {analytic.stats.map((stat, index2) => {
                return (
                  <div key={`${index2}`} className={styles.cardStatContainer}>
                    <div className={styles.cardStatLabel}>{stat.label}</div>
                    <div className={styles.cardStatValue}>
                      {isFetchingAnalytics || isFetchingInventory ? <Skeleton type='line-short' /> : stat.value}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <InventoryTable />
      <div className={styles.containerTimeStamp} >
        <LastUpdatedTimeStamp timestamp={analytics?.lastSynced} />
      </div>
    </div>
  );
};

export default InventoryPage;
